import classNames from 'classnames'
import React, {
  PropsWithChildren,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { SwipeDetector } from '../SwipeDetector'
import {
  ControlledSlideshowNavigation,
  SlideshowDotsColor,
  SlideshowTimer,
} from './ControlledSlideshowNavigation'

export interface SlideshowContainerProps {
  children: ReactNode[]

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref: RefObject<any>
}

export interface SlideshowProps {
  dotsColor?: SlideshowDotsColor
  container?: React.FC<SlideshowContainerProps>
  navigationButtonsClassName?: string
  timer: SlideshowTimer
  /**
   * Set container size.
   * 'tallestSlide' (default) sets height based on tallest slide
   * 'grow' uses flex to allow container to grow as much as the parent component allows
   * 'auto' sets "height" to auto in CSS
   */
  size?: 'tallestSlide' | 'grow' | 'auto'
  transitionStyle?: 'crossfade' | 'overexpose'
}

export const Slideshow = ({
  children,
  container,
  dotsColor = SlideshowDotsColor.Variant1,
  navigationButtonsClassName = 'absolute left-1/2 transform -translate-x-1/2 bottom-0',
  size = 'tallestSlide',
  timer,
  transitionStyle = 'crossfade',
}: PropsWithChildren<SlideshowProps>) => {
  const slidesContainerRef = useRef<HTMLDivElement>(null)
  const resizeRef = useRef<HTMLElement>(null)
  const [currentSlide, setCurrentSlide] = useState<number>(0)
  const [paused, setPaused] = useState(false)
  const childrenArray = Array.isArray(children) ? children : [children]

  const hasMultipleSlides = childrenArray.length > 1

  /** Sets container height based on tallest content */
  const resize = useCallback(() => {
    if (
      hasMultipleSlides &&
      slidesContainerRef.current &&
      resizeRef?.current &&
      size === 'tallestSlide'
    ) {
      const slides = slidesContainerRef.current.querySelectorAll('.slide')

      if (!slides) {
        throw Error('Unable to resolve slide elements')
      }

      const resizeElement = resizeRef.current

      const resizeElementStyle = window.getComputedStyle(resizeElement)

      const topPadding = parseInt(resizeElementStyle.getPropertyValue('padding-top'))

      const bottomPadding = parseInt(
        resizeElementStyle.getPropertyValue('padding-bottom')
      )

      const maxSlideHeight = Math.max(
        ...Array.from(slides).map(slide => (slide as HTMLElement).offsetHeight)
      )

      // Set height based on tallest content, corrected for outer box padding
      slidesContainerRef.current.style.height = `${
        maxSlideHeight + (topPadding + bottomPadding * 2)
      }px`
    }
  }, [hasMultipleSlides, resizeRef, size])

  useEffect(() => {
    if (size === 'tallestSlide') {
      resize()
    }

    window.addEventListener('resize', resize)

    return () => window.removeEventListener('resize', resize)
  }, [resize, size, timer])

  const processedChildren = childrenArray.map((slide, i) => {
    let className = 'slide top-0'

    if (hasMultipleSlides) {
      if (transitionStyle === 'overexpose') {
        className += ' transition duration-700 ease-out'
        let exposureClassName =
          'mix-blend-overlay bg-neutral absolute inset-0 z-20 pointer-events-none transition'

        if (i === currentSlide) {
          className += ' delay-700 opacity-100 z-20'
          exposureClassName += ' duration-0 opacity-0'
        } else {
          className += ' delay-200 opacity-0 absolute w-full pointer-events-none z-10'
          exposureClassName += ' duration-700 opacity-100'
        }

        return (
          <div key={i} className={className}>
            <div className={exposureClassName} />
            {slide}
          </div>
        )
      } else {
        className += ' transition duration-1000 ease-in-out'

        if (i === currentSlide) {
          // The one to fade in
          className += ' opacity-100 z-20'
        } else {
          className += ' opacity-0 absolute w-full pointer-events-none z-10'
        }

        return (
          <div key={i} className={className}>
            {slide}
          </div>
        )
      }
    }

    return (
      <div key={i} className={className}>
        {slide}
      </div>
    )
  })

  const onNavigate = useCallback(
    (index: number) => {
      if (index < 0) {
        setCurrentSlide(childrenArray.length - 1)
      } else if (index >= childrenArray.length) {
        setCurrentSlide(0)
      } else {
        setCurrentSlide(index)
      }
    },
    [childrenArray.length]
  )

  const onNavigatePrevious = useCallback(() => {
    onNavigate(currentSlide - 1)
  }, [currentSlide, onNavigate])

  const onNavigateNext = useCallback(() => {
    onNavigate(currentSlide + 1)
  }, [currentSlide, onNavigate])

  if (hasMultipleSlides) {
    processedChildren.push(
      <div className={navigationButtonsClassName} key="navigation-buttons">
        <ControlledSlideshowNavigation
          color={dotsColor}
          currentIndex={currentSlide}
          numberOfDots={childrenArray.length}
          onNavigate={onNavigate}
          onNavigatePrevious={onNavigatePrevious}
          onNavigateNext={onNavigateNext}
          paused={paused}
          timer={timer}
        />
      </div>
    )
  }

  return (
    <SwipeDetector
      eventTarget={slidesContainerRef}
      onSwipeLeft={onNavigateNext}
      onSwipeRight={onNavigatePrevious}
    >
      <div
        ref={slidesContainerRef}
        className={classNames('h-full', {
          'flex-1': size === 'grow',
        })}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {container
          ? container({ children: processedChildren, ref: resizeRef })
          : processedChildren}
      </div>
    </SwipeDetector>
  )
}
